import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { useNavigate, Link } from 'react-router-dom';
import { getUserInfo } from '../services/userService';
import { listFranchiseeJobs, formatTimestamp, formatFrequency, formatAllocationType, formatAllocationDetails, getGoogleMapsUrl, listJobSchedules, submitQuote } from '../services/jobService';
import { getRctiPdfUrl } from '../services/rctiService';
import { getFranchiseeHouseholds } from '../services/householdService';
import { useConfig } from '../context/ConfigContext';
import { useAdminView } from '../context/AdminViewContext';
import { useSuspension } from '../context/SuspensionContext';
import StatusBadge from '../components/StatusBadge';
import PendingQuotationList from '../components/PendingQuotationList';
import ComplianceTab from '../components/ComplianceTab';
import { FaCalendarAlt, FaClock, FaUser, FaHome, FaMapMarkerAlt, FaTasks, FaRedo, FaTags, FaHourglassHalf, FaCommentAlt, FaStickyNote, FaCalendarCheck, FaFilter, FaTimes, FaExclamationTriangle, FaCheckCircle, FaDollarSign, FaInfoCircle, FaUserTie, FaLayerGroup, FaFileInvoiceDollar, FaChevronDown, FaUserCircle, FaSignOutAlt, FaPhoneAlt } from 'react-icons/fa';
import { MdOutlineImageNotSupported } from 'react-icons/md';
import AccountModal from '../components/modals/AccountModal';
import { terminology } from '../config/terminology';

// Build a display label for a household option (name + primary address line).
const householdLabel = (h) => {
  const name = `${h.householdInfo?.firstName || ''} ${h.householdInfo?.lastName || ''}`.trim() || 'N/A';
  const address = h.householdInfo?.addressLine1 ? ` (${h.householdInfo.addressLine1})` : '';
  return `${name}${address}`;
};

// Normalizes a job's date field (Firestore Timestamp, serialized {_seconds}, ISO string, or Date) to a Date, or null if absent/unparsable.
const getJobDateValue = (job, field) => {
  const val = job?.[field];
  if (!val) return null;
  if (typeof val.toDate === 'function') return val.toDate();
  if (typeof val._seconds === 'number') return new Date(val._seconds * 1000);
  if (val instanceof Date) return val;
  if (typeof val === 'string') {
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

// Combined sort-field/direction options for the "Sort by" control, matching the value format `${field}-${direction}`.
const JOB_SORT_OPTIONS = [
  { value: '', label: 'Default Order' },
  { value: 'jobDate-asc', label: 'Scheduled Date (Oldest First)' },
  { value: 'jobDate-desc', label: 'Scheduled Date (Newest First)' },
  { value: 'completionDate-asc', label: 'Completed Date (Oldest First)' },
  { value: 'completionDate-desc', label: 'Completed Date (Newest First)' },
];
const DEFAULT_JOB_SORT = '';
const defaultSortForStatus = (status) => (status === 'approved' || status === '' ? '' : 'jobDate-asc');

const Home = () => {
  const navigate = useNavigate();
  const { statuses: configStatuses, loadingConfig, configError } = useConfig();
  const { isAdminView, adminSelectedFseId, adminSelectedFranchiseeName, endAdminView } = useAdminView();
  const { isSuspended } = useSuspension();
  const [userInfo, setUserInfo] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loadingUser, setLoadingUser] = useState(true);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [userError, setUserError] = useState(null);
  const [jobsError, setJobsError] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('approved');
  const [activeTab, setActiveTab] = useState('myJobs');
  const [jobSortOption, setJobSortOption] = useState(DEFAULT_JOB_SORT);
  const [missedInfo, setMissedInfo] = useState({ truncated: false, total: 0 });

  // Per-job RCTI link state: { [jobId]: { loading: boolean, error: string|null } }
  const [rctiStatus, setRctiStatus] = useState({});

  // Add state for quote confirmation modal
  const [showQuoteConfirmation, setShowQuoteConfirmation] = useState(false);
  const [quoteToConfirm, setQuoteToConfirm] = useState(null);

  // Account modal (franchisee's own profile + change password)
  const [showAccountModal, setShowAccountModal] = useState(false);

  // --- States for "Pending Your Quotation" (previously Pending Quotes) ---
  const [pendingYourQuotationSchedules, setPendingYourQuotationSchedules] = useState([]);
  const [loadingPendingYourQuotation, setLoadingPendingYourQuotation] = useState(false);
  const [pendingYourQuotationError, setPendingYourQuotationError] = useState(null);
  const [nextPageDocIdPendingYourQuotation, setNextPageDocIdPendingYourQuotation] = useState(null);
  const [initialPendingYourQuotationLoaded, setInitialPendingYourQuotationLoaded] = useState(false);

  // --- States for "Pending Approval" ---
  const [activeQuoteSubTab, setActiveQuoteSubTab] = useState('pendingYourQuotation'); // 'pendingYourQuotation' or 'pendingApproval'
  const [pendingApprovalSchedules, setPendingApprovalSchedules] = useState([]);
  const [loadingPendingApproval, setLoadingPendingApproval] = useState(false);
  const [pendingApprovalError, setPendingApprovalError] = useState(null);
  const [nextPageDocIdPendingApproval, setNextPageDocIdPendingApproval] = useState(null);
  const [initialPendingApprovalLoaded, setInitialPendingApprovalLoaded] = useState(false);
  
  // Add state for price per hour inputs
  const [pricePerHourInputs, setPricePerHourInputs] = useState({});
  const [quoteSubmitError, setQuoteSubmitError] = useState({}); // State for individual quote errors
  const [isSubmittingQuote, setIsSubmittingQuote] = useState({}); // State for individual quote submission loading

  // Add state for Notes for Admin inputs
  const [notesForAdminInputs, setNotesForAdminInputs] = useState({});

  // Add state for Frequency inputs
  const [frequencyInputs, setFrequencyInputs] = useState({});

  // Update price per hour for a specific schedule
  const handlePricePerHourChange = (scheduleId, value) => {
    setPricePerHourInputs(prev => ({
      ...prev,
      [scheduleId]: value
    }));
  };

  // Update notes for admin for a specific schedule
  const handleNotesForAdminChange = (scheduleId, value) => {
    setNotesForAdminInputs((prev) => ({ ...prev, [scheduleId]: value }));
  };

  // Update frequency for a specific schedule
  const handleFrequencyChange = (scheduleId, field, value) => {
    setFrequencyInputs((prev) => ({
      ...prev,
      [scheduleId]: {
        ...prev[scheduleId],
        [field]: value,
        // Auto-select interval option when user types in interval fields
        ...(field === 'intervalValue' || field === 'intervalUnit' ? { option: 'interval' } : {})
      }
    }));
  };

  // Initialize price per hour inputs when schedules change (for 'pendingYourQuotation')
  useEffect(() => {
    const initialPrices = {};
    pendingYourQuotationSchedules.forEach(schedule => {
      initialPrices[schedule.id] = schedule.pricePerHour || '';
    });
    setPricePerHourInputs(initialPrices);
  }, [pendingYourQuotationSchedules]);

  // Initialize notes for admin inputs when schedules change (for 'pendingYourQuotation')
  useEffect(() => {
    const initialNotes = {};
    pendingYourQuotationSchedules.forEach((s) => {
      initialNotes[s.id] = '';
    });
    setNotesForAdminInputs(initialNotes);
  }, [pendingYourQuotationSchedules]);

  // Initialize frequency inputs when schedules change (for 'pendingYourQuotation')
  useEffect(() => {
    const initialFrequency = {};
    pendingYourQuotationSchedules.forEach((schedule) => {
      // Initialize from existing data or empty values
      const existingFrequencyType = schedule.frequencyType?.toLowerCase();
      const existingFrequencyValue = schedule.frequencyValue;
      
      if (existingFrequencyType === 'monthly') {
        initialFrequency[schedule.id] = {
          option: 'monthly',
          intervalValue: '',
          intervalUnit: 'days'
        };
      } else if (existingFrequencyType === 'days' || existingFrequencyType === 'weeks') {
        initialFrequency[schedule.id] = {
          option: 'interval',
          intervalValue: existingFrequencyValue || '',
          intervalUnit: existingFrequencyType
        };
      } else {
        // No existing frequency data, start empty
        initialFrequency[schedule.id] = {
          option: '',
          intervalValue: '',
          intervalUnit: 'days'
        };
      }
    });
    setFrequencyInputs(initialFrequency);
  }, [pendingYourQuotationSchedules]);

  // --- Pagination State for My Jobs ---
  const [nextPageDocId, setNextPageDocId] = useState(null);
  const [initialJobsLoaded, setInitialJobsLoaded] = useState(false); // Track if initial jobs load completed

  // --- Filter State ---
  const [filterHouseholdId, setFilterHouseholdId] = useState(null);
  const [noMoreJobsMessage, setNoMoreJobsMessage] = useState(''); // Added for no more jobs message
  const [householdSearchTerm, setHouseholdSearchTerm] = useState(''); // Combobox input value (typed query / selected label)
  const filterDropdownRef = useRef(null); // Ref for closing dropdowns
  const selectedHouseholdIdRef = useRef(null); // Latest selection, for the blur handler (avoids stale closure)

  // Preloaded household options for the filter dropdown
  const [householdOptions, setHouseholdOptions] = useState([]);
  const [loadingHouseholds, setLoadingHouseholds] = useState(false);
  const [householdsError, setHouseholdsError] = useState(null);
  const [householdMenuOpen, setHouseholdMenuOpen] = useState(false);
  const [householdReloadKey, setHouseholdReloadKey] = useState(0); // Bump to retry preload
  // --- End Filter State ---

  // Client-side filtered household options based on the typed query.
  // When the input still shows the selected label, show the full list.
  const filteredHouseholds = useMemo(() => {
    const q = householdSearchTerm.trim().toLowerCase();
    const selected = householdOptions.find(h => h.householdId === filterHouseholdId);
    const showingSelection = selected && householdLabel(selected) === householdSearchTerm;
    if (!q || showingSelection) return householdOptions;
    return householdOptions.filter(h => {
      const haystack = `${householdLabel(h)} ${h.householdInfo?.state || ''} ${h.householdInfo?.postalCode || ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [householdSearchTerm, householdOptions, filterHouseholdId]);

  // Keep a ref of the current selection so the deferred blur handler reads the latest value.
  selectedHouseholdIdRef.current = filterHouseholdId;

  // Memoize the status options for the filter dropdown, adding "All Statuses"
  const statusFilterOptions = useMemo(() => {
    // Filter out 'pending-quote' and 'pending-approval' statuses from the dropdown only
    const filteredStatuses = configStatuses.filter(s => 
      s.code !== 'pending-quote' && s.code !== 'pending-approval'
    );
    
    // Map the filtered statuses to options
    const options = filteredStatuses.map(s => ({ value: s.code, label: s.display }));
    
    // Add "All Statuses" at the beginning
    return [{ value: '', label: 'All Statuses' }, ...options];
  }, [configStatuses]);

  // Fetch User Info Effect
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setLoadingUser(true);
        setUserError(null);
        try {
          const idToken = await user.getIdToken();
          const fetchedUserInfo = await getUserInfo(idToken);
          setUserInfo(fetchedUserInfo);
        } catch (err) {
          console.error('Failed to fetch user info:', err);
          setUserError(err.message || 'Could not load user data.');
        } finally {
          setLoadingUser(false);
        }
      } else {
        setUserInfo(null);
        setLoadingUser(false);
        navigate('/login');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // Fetch Jobs Effect (runs when userInfo, selectedStatus, or filterHouseholdId changes)
  const fetchJobs = useCallback(
    async (filters = {}, loadMore = false) => {
      // Check if userInfo exists and has a userType
      if (!userInfo || !userInfo.userType) {
        console.log("fetchJobs aborted: userInfo or userType missing.");
        return;
      }

      // Check fseId only if user is NOT admin
      if (userInfo.userType !== 'admin' && !userInfo.fseId) {
        console.log("fetchJobs aborted: Non-admin user missing fseId.");
        return;
      }

      console.log("fetchJobs executing for user:", userInfo.uid, "Type:", userInfo.userType, "LoadMore:", loadMore);

      setLoadingJobs(true);
      setJobsError(null);
      if (!loadMore) { // Reset message on initial load or filter change
        setNoMoreJobsMessage('');
      }

      try {
        // All filtering/aggregation is done server-side; we just request what to display.
        const fseId = isAdminView ? adminSelectedFseId : undefined;
        const status = filters.status !== undefined ? filters.status : selectedStatus;
        const householdId = filters.householdId !== undefined ? filters.householdId : filterHouseholdId;

        const options = {
          status,
          sort: jobSortOption,
        };
        if (fseId) options.fseId = fseId;
        if (householdId) options.householdId = householdId;
        if (loadMore && nextPageDocId) {
          options.pageSize = 10;
          options.startAfterDocId = nextPageDocId;
        }

        const data = await listFranchiseeJobs(options);

        if (loadMore) {
          if (data.jobs && data.jobs.length > 0) {
            setJobs(prevJobs => [...prevJobs, ...(data.jobs || [])]);
            setNoMoreJobsMessage('');
          } else {
            setNoMoreJobsMessage('No more jobs to load.');
          }
        } else {
          setJobs(data.jobs || []);
          setMissedInfo({ truncated: !!data.missedTruncated, total: data.missedTotal != null ? data.missedTotal : 0 });
          if (!initialJobsLoaded) setInitialJobsLoaded(true);
        }

        setNextPageDocId(data.nextPageDocId || null);
      } catch (err) {
        console.error('Failed to fetch jobs:', err);
        setJobsError(err.message || 'Could not load jobs.');
        if (!loadMore) {
          setJobs([]);
          setNextPageDocId(null);
          setNoMoreJobsMessage('');
        }
      } finally {
        setLoadingJobs(false);
      }
    },
    // Dependencies: userInfo object itself, selectedStatus, filterHouseholdId, nextPageDocId, initialJobsLoaded
    // nextPageDocId is needed here because fetchJobs uses it to decide the startAfterDocId for loadMore.
    // setNoMoreJobsMessage is a setter, stable.
    [userInfo, selectedStatus, filterHouseholdId, jobSortOption, nextPageDocId, initialJobsLoaded, isAdminView, adminSelectedFseId]
  );

  // Generalized fetch function for schedules by quote status
  const fetchSchedulesByQuoteStatus = useCallback(async (quoteStatus, loadMore = false) => {
    const effectiveFseId = isAdminView ? adminSelectedFseId : (userInfo?.fseId || null);
    if (!userInfo || !effectiveFseId) {
      if (quoteStatus === 'pending-quote') {
        setPendingYourQuotationSchedules([]);
        setNextPageDocIdPendingYourQuotation(null);
        setInitialPendingYourQuotationLoaded(true);
      } else if (quoteStatus === 'pending-approval') {
        setPendingApprovalSchedules([]);
        setNextPageDocIdPendingApproval(null);
        setInitialPendingApprovalLoaded(true);
      }
      return;
    }

    const isLoadingSetter = quoteStatus === 'pending-quote' ? setLoadingPendingYourQuotation : setLoadingPendingApproval;
    const errorSetter = quoteStatus === 'pending-quote' ? setPendingYourQuotationError : setPendingApprovalError;
    const schedulesSetter = quoteStatus === 'pending-quote' ? setPendingYourQuotationSchedules : setPendingApprovalSchedules;
    const nextPageDocIdSetter = quoteStatus === 'pending-quote' ? setNextPageDocIdPendingYourQuotation : setNextPageDocIdPendingApproval;
    const currentNextPageDocId = quoteStatus === 'pending-quote' ? nextPageDocIdPendingYourQuotation : nextPageDocIdPendingApproval;
    const initialLoadedFlag = quoteStatus === 'pending-quote' ? initialPendingYourQuotationLoaded : initialPendingApprovalLoaded;
    const setInitialLoadedFlag = quoteStatus === 'pending-quote' ? setInitialPendingYourQuotationLoaded : setInitialPendingApprovalLoaded;


    isLoadingSetter(true);
    errorSetter(null);

    try {
      const options = {
        fseId: effectiveFseId,
        quoteStatus: quoteStatus,
        pageSize: 10,
        orderBy: 'startDate',
        orderDirection: 'asc',
        startAfterDocId: loadMore ? currentNextPageDocId : null,
      };
      const data = await listJobSchedules(options);

      schedulesSetter(prevSchedules =>
        loadMore ? [...prevSchedules, ...(data.schedules || [])] : (data.schedules || [])
      );
      nextPageDocIdSetter(data.schedules && data.schedules.length >= 10 ? data.nextPageDocId : null);
    } catch (err) {
      console.error(`Failed to fetch ${quoteStatus} schedules:`, err);
      errorSetter(err.message || `Could not load ${quoteStatus} schedules.`);
      if (!loadMore) schedulesSetter([]);
    } finally {
      isLoadingSetter(false);
      if (!initialLoadedFlag) { // Set initial loaded flag only once after the first fetch attempt
        setInitialLoadedFlag(true);
      }
    }
  }, [userInfo, nextPageDocIdPendingYourQuotation, nextPageDocIdPendingApproval]); // Removed initialLoaded flags from here as they are set inside

  // Fetch Pending Quote Schedules Effect (renamed to fetchSchedulesByQuoteStatus)
  // const fetchPendingQuotes = useCallback(async (loadMore = false) => { // This function is now fetchSchedulesByQuoteStatus
  // ...
  // }, [userInfo, nextPageDocIdPendingQuotes, initialPendingQuotesLoaded]);


  useEffect(() => {
    if (userInfo) {
      // console.log("useEffect triggered: Fetching jobs for user:", userInfo.uid, "Type:", userInfo.userType);
      // This is an initial load or a load due to filter changes. loadMore is false.
      fetchJobs({ status: selectedStatus, householdId: filterHouseholdId }, false);
    } else {
      // console.log("useEffect triggered: userInfo not available yet.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo, selectedStatus, filterHouseholdId, jobSortOption]); // fetchJobs is intentionally omitted from deps here

  // useEffect for preloading 'pending-quote' for the welcome message
  useEffect(() => {
    if (userInfo && !initialPendingYourQuotationLoaded) {
      fetchSchedulesByQuoteStatus('pending-quote', false);
    }
  }, [userInfo, initialPendingYourQuotationLoaded, fetchSchedulesByQuoteStatus]);

  // useEffect for fetching data based on active MAIN tab and active SUB tab for Quotes
  useEffect(() => {
    if (userInfo && activeTab === 'pendingQuotes') {
      if (activeQuoteSubTab === 'pendingYourQuotation' && !initialPendingYourQuotationLoaded) {
        fetchSchedulesByQuoteStatus('pending-quote', false);
      } else if (activeQuoteSubTab === 'pendingApproval' && !initialPendingApprovalLoaded) {
        fetchSchedulesByQuoteStatus('pending-approval', false);
      }
    }
  }, [userInfo, activeTab, activeQuoteSubTab, initialPendingYourQuotationLoaded, initialPendingApprovalLoaded, fetchSchedulesByQuoteStatus]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
        setHouseholdMenuOpen(false);
      }
    };
    if (householdMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [householdMenuOpen]);

  // Preload the franchisee's households for the filter dropdown (once per session / retry).
  useEffect(() => {
    let cancelled = false;
    const loadHouseholds = async () => {
      const effectiveFseId = isAdminView ? adminSelectedFseId : (userInfo?.fseId || null);
      if (!userInfo || (!isAdminView && userInfo.userType === 'admin') || !effectiveFseId) return;
      setLoadingHouseholds(true);
      setHouseholdsError(null);
      try {
        const households = await getFranchiseeHouseholds(effectiveFseId);
        if (!cancelled) setHouseholdOptions(households);
      } catch (err) {
        console.error('Failed to preload households for filter:', err);
        if (!cancelled) {
          setHouseholdOptions([]);
          setHouseholdsError(err.message || 'Could not load households.');
        }
      } finally {
        if (!cancelled) setLoadingHouseholds(false);
      }
    };
    loadHouseholds();
    return () => { cancelled = true; };
  }, [userInfo, householdReloadKey, isAdminView, adminSelectedFseId]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUserInfo(null);
      setJobs([]);
      setInitialJobsLoaded(false);
      setPendingYourQuotationSchedules([]);
      setNextPageDocIdPendingYourQuotation(null);
      setInitialPendingYourQuotationLoaded(false);
      setPendingApprovalSchedules([]);
      setNextPageDocIdPendingApproval(null);
      setInitialPendingApprovalLoaded(false);
      setUserError(null);
      setJobsError(null);
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      setUserError('Failed to logout.');
    }
  };

  const handleStatusFilterChange = (event) => {
    const newStatus = event.target.value;
    setSelectedStatus(newStatus);
    setJobSortOption(defaultSortForStatus(newStatus));
  };

  const handleJobSortChange = (event) => {
    setJobSortOption(event.target.value);
  };

  // Open the RCTI PDF in a new tab. Stops the parent card Link from navigating.
  const handleViewRcti = async (event, job) => {
    event.preventDefault();
    event.stopPropagation();
    if (!job.rctiId) return;

    // Open the tab synchronously so popup blockers don't suppress it, then
    // navigate it once the signed URL resolves.
    const newTab = window.open('', '_blank');
    setRctiStatus(prev => ({ ...prev, [job.id]: { loading: true, error: null } }));
    try {
      const url = await getRctiPdfUrl(job.rctiId);
      if (url) {
        if (newTab) newTab.location.href = url; else window.open(url, '_blank');
        setRctiStatus(prev => ({ ...prev, [job.id]: { loading: false, error: null } }));
      } else {
        if (newTab) newTab.close();
        setRctiStatus(prev => ({ ...prev, [job.id]: { loading: false, error: 'RCTI is still being generated, try again shortly.' } }));
      }
    } catch (err) {
      if (newTab) newTab.close();
      console.error('Failed to open RCTI:', err);
      setRctiStatus(prev => ({ ...prev, [job.id]: { loading: false, error: err.message || 'Could not open RCTI.' } }));
    }
  };

  // --- Filter Handlers ---
  const handleClearFilters = () => {
    setSelectedStatus('approved'); // Reset to the default 'Approved' view on clear
    setFilterHouseholdId(null);
    setHouseholdSearchTerm('');
    setHouseholdMenuOpen(false);
    setJobSortOption(DEFAULT_JOB_SORT);
  };

  // Household combobox handlers (client-side filtering over the preloaded list)
  const handleHouseholdInputChange = (e) => {
    setHouseholdSearchTerm(e.target.value);
    setFilterHouseholdId(null); // typing clears any active selection until a new one is picked
    setHouseholdMenuOpen(true);
  };

  const handleHouseholdSelect = (h) => {
    setFilterHouseholdId(h.householdId);
    setHouseholdSearchTerm(householdLabel(h));
    setHouseholdMenuOpen(false);
  };

  const handleHouseholdClear = () => {
    setFilterHouseholdId(null);
    setHouseholdSearchTerm('');
    setHouseholdMenuOpen(false);
  };

  const handleHouseholdBlur = () => {
    // Defer so an option's onMouseDown registers before the menu closes.
    setTimeout(() => {
      setHouseholdMenuOpen(false);
      // If nothing is selected, clear leftover typed text so the field stays tidy.
      if (!selectedHouseholdIdRef.current) setHouseholdSearchTerm('');
    }, 150);
  };
  // --- End Filter Handlers ---

  const handleLoadMorePendingYourQuotation = () => {
    if (nextPageDocIdPendingYourQuotation && !loadingPendingYourQuotation) {
      fetchSchedulesByQuoteStatus('pending-quote', true);
    }
  };

  const handleLoadMorePendingApproval = () => {
    if (nextPageDocIdPendingApproval && !loadingPendingApproval) {
      fetchSchedulesByQuoteStatus('pending-approval', true);
    }
  };

  // Function to load more jobs in the My Jobs tab
  const handleLoadMoreJobs = () => {
    if (nextPageDocId && !loadingJobs) {
      fetchJobs({}, true);
    }
  };

  // Helper function to render schedule date for pending quotes
  const renderScheduleDetails = (schedule) => {
    if (!schedule || !schedule.startDate) return 'N/A';
    return formatTimestamp(schedule.startDate);
  };

  const handleQuoteSubmit = async (scheduleId, pricePerHour) => {
    // Get frequency data for this schedule
    const frequencyData = frequencyInputs[scheduleId] || {};
    
    // Show confirmation modal instead of submitting directly
    setQuoteToConfirm({ 
      scheduleId, 
      pricePerHour, 
      notesFromFranchisee: notesForAdminInputs[scheduleId],
      frequency: frequencyData
    });
    setShowQuoteConfirmation(true);
  };

  const confirmAndSubmitQuote = async () => {
    if (!quoteToConfirm) return;
    
    const { scheduleId, pricePerHour, notesFromFranchisee, frequency } = quoteToConfirm;
    setQuoteSubmitError(prev => ({ ...prev, [scheduleId]: null })); 
    setIsSubmittingQuote(prev => ({ ...prev, [scheduleId]: true })); 
    setShowQuoteConfirmation(false); // Hide modal

    try {
      // Prepare frequency data for API
      let frequencyType = null;
      let frequencyValue = null;
      
      if (frequency && frequency.option === 'monthly') {
        frequencyType = 'monthly';
      } else if (frequency && frequency.option === 'interval' && frequency.intervalValue && frequency.intervalUnit) {
        // Validate interval value
        const intervalValue = parseInt(frequency.intervalValue);
        if (intervalValue && intervalValue > 0 && Number.isInteger(intervalValue)) {
          frequencyType = frequency.intervalUnit === 'days' ? 'days' : 'weeks';
          frequencyValue = intervalValue;
        }
      }

      console.log('Submitting quote with frequency data:', { scheduleId, pricePerHour, notesFromFranchisee, frequencyType, frequencyValue });

      await submitQuote(scheduleId, pricePerHour, notesFromFranchisee, frequencyType, frequencyValue);
      fetchSchedulesByQuoteStatus('pending-quote', false);
      setPricePerHourInputs(prev => ({ ...prev, [scheduleId]: '' }));
      setNotesForAdminInputs(prev => ({ ...prev, [scheduleId]: '' }));
      setFrequencyInputs(prev => ({ ...prev, [scheduleId]: { option: '', intervalValue: '', intervalUnit: 'days' } }));
    } catch (error) {
      console.error('Failed to submit quote:', error);
      setQuoteSubmitError(prev => ({ 
        ...prev, 
        [scheduleId]: error.message || 'Failed to submit quote. Please try again.' 
      }));
    } finally {
      setIsSubmittingQuote(prev => ({ ...prev, [scheduleId]: false }));
      setQuoteToConfirm(null);
    }
  };

  // Show tabs once initial data load completes - don't hide tabs based on filter results
  const hasAnyData = initialJobsLoaded || pendingYourQuotationSchedules.length > 0 || pendingApprovalSchedules.length > 0;
  const showJobTabs = hasAnyData;

  useEffect(() => {
    if (!showJobTabs) {
      setActiveTab('compliance');
    } else {
      setActiveTab('myJobs'); 
    }
  }, [showJobTabs]);

  // Add handlers for tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'myJobs') {
      fetchJobs({ status: selectedStatus, householdId: filterHouseholdId }, false);
    } else if (tab === 'pendingQuotes') {
      // Reset to default sub-tab and fetch its data
      setActiveQuoteSubTab('pendingYourQuotation');
      fetchSchedulesByQuoteStatus('pending-quote', false);
    }
  };

  const handleQuoteSubTabChange = (subTab) => {
    setActiveQuoteSubTab(subTab);
    if (subTab === 'pendingYourQuotation') {
      fetchSchedulesByQuoteStatus('pending-quote', false);
    } else if (subTab === 'pendingApproval') {
      fetchSchedulesByQuoteStatus('pending-approval', false);
    }
  };

  if (loadingUser || loadingConfig) { // Check for config loading as well
    return <div className="container mx-auto px-4 py-16 text-center">Loading user data...</div>;
  }

  if (userError || configError) { // Check for config error
    return <div className="container mx-auto px-4 py-16 text-center text-red-600">Error: {userError || configError}</div>;
  }

  const handleAdminLogout = async () => {
    endAdminView();
    try {
      await signOut(auth);
    } catch (e) { console.error(e); }
    navigate('/admin-login');
  };

  return (
    <div className="container mx-auto px-4 py-8 md:py-16">
      <div className="max-w-4xl mx-auto">
        {/* Admin View Banner */}
        {isAdminView && (
          <div className="bg-blue-600 text-white px-4 py-2 rounded-lg mb-4 flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm">
              Viewing as <span className="font-bold">{adminSelectedFranchiseeName}</span> (read-only)
            </span>
            <button
              onClick={handleAdminLogout}
              className="px-3 py-1 text-xs font-medium text-blue-600 bg-white rounded hover:bg-blue-50 transition"
            >
              Switch franchisee
            </button>
          </div>
        )}
        <div className="flex justify-between items-center mb-6 md:mb-8">
          <h1 className="text-xl md:text-3xl font-bold text-green-700">
            <span className="hidden md:inline">Loope Franchisee Dashboard</span>
            <span className="md:hidden">Loope</span>
          </h1>
          {!isAdminView ? (
            <div className="flex items-center gap-2 md:gap-3">
              <button
                onClick={() => setShowAccountModal(true)}
                aria-label="Account"
                className="flex items-center px-3 py-1.5 md:px-4 md:py-2 text-xs md:text-sm font-medium text-white bg-green-600 border border-transparent rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                <FaUserCircle className="text-base md:hidden" />
                <span className="hidden md:inline">Account</span>
              </button>
              <button
                onClick={handleLogout}
                aria-label="Logout"
                className="flex items-center px-3 py-1.5 md:px-4 md:py-2 text-xs md:text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <FaSignOutAlt className="text-base md:hidden" />
                <span className="hidden md:inline">Logout</span>
              </button>
            </div>
          ) : (
            <button
              onClick={handleAdminLogout}
              className="px-3 py-1.5 md:px-4 md:py-2 text-xs md:text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Exit View
            </button>
          )}
        </div>
        {showAccountModal && (
          <AccountModal
            franchisee={userInfo?.franchiseeData}
            onClose={() => setShowAccountModal(false)}
          />
        )}

        {/* Updated Welcome Message Styling */}
        <div className="mb-6 md:mb-8">
          <p className="text-base md:text-xl text-gray-700 text-left">Welcome, {userInfo?.firstName || 'Franchisee'}!</p>
          {userInfo && initialPendingYourQuotationLoaded && (
            <p className="text-sm text-gray-600 mt-1">
              You have {jobs.filter(job => job.status === 'approved').length} approved job(s) and {pendingYourQuotationSchedules.length} job(s) pending quotation. Please submit your quote(s) as soon as possible.
            </p>
          )}
        </div>

        {/* Tabs Navigation */}
        <div className="mb-4 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            {showJobTabs && (
              <>
                <button
                  onClick={() => handleTabChange('myJobs')}
                  className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'myJobs'
                      ? 'border-green-500 text-green-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  My Jobs
                </button>
                <button
                  onClick={() => handleTabChange('pendingQuotes')}
                  className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'pendingQuotes'
                      ? 'border-green-500 text-green-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Quote Pending
                </button>
              </>
            )}
            <button
              onClick={() => handleTabChange('compliance')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'compliance'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Compliance
            </button>
          </nav>
        </div>

        {activeTab === 'myJobs' && (
          <>
            <div ref={filterDropdownRef} className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4 items-end"> {/* Added mb-4 for spacing */}
              <div>
                <label htmlFor="status-filter" className="block text-xs font-medium text-gray-600">
                  Status
                </label>
                <select
                  id="status-filter"
                  name="status-filter"
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-sm md:text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md shadow-sm disabled:bg-gray-100 disabled:text-gray-400"
                  value={selectedStatus}
                  onChange={handleStatusFilterChange}
                  disabled={loadingJobs || loadingConfig}
                >
                  {/* Use memoized status options */}
                  {statusFilterOptions.map((statusOption) => (
                    <option key={statusOption.value} value={statusOption.value}>
                      {statusOption.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="householdFilter" className="block text-xs font-medium text-gray-600">
                  {terminology.household.singular}
                </label>
                <div className="relative mt-1">
                  <input
                    id="householdFilter"
                    type="text"
                    role="combobox"
                    aria-expanded={householdMenuOpen}
                    aria-controls="householdFilter-listbox"
                    aria-autocomplete="list"
                    autoComplete="off"
                    value={householdSearchTerm}
                    onChange={handleHouseholdInputChange}
                    onFocus={() => setHouseholdMenuOpen(true)}
                    onBlur={handleHouseholdBlur}
                    placeholder={
                      loadingHouseholds
                        ? 'Loading…'
                        : householdOptions.length === 0
                          ? `No ${terminology.household.plural.toLowerCase()}`
                          : `Select or search ${terminology.household.plural.toLowerCase()}...`
                    }
                    className="block w-full pl-3 pr-9 py-2 text-sm md:text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:text-gray-400"
                    disabled={loadingJobs || loadingHouseholds || householdOptions.length === 0}
                  />
                  {/* Trailing affordance: clear when a selection is made, otherwise a chevron */}
                  {filterHouseholdId ? (
                    <button
                      type="button"
                      onClick={handleHouseholdClear}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                      title={`Clear ${terminology.household.singular.toLowerCase()}`}
                      aria-label="Clear selection"
                      disabled={loadingJobs}
                    >
                      <FaTimes />
                    </button>
                  ) : (
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400">
                      <FaChevronDown className={`transition-transform ${householdMenuOpen ? 'rotate-180' : ''}`} />
                    </span>
                  )}
                  {householdMenuOpen && householdOptions.length > 0 && (
                    <ul id="householdFilter-listbox" role="listbox" className="absolute z-10 w-full bg-white border border-gray-300 rounded-md mt-1 max-h-60 overflow-auto shadow-lg suggestions-list">
                      {filteredHouseholds.length > 0 ? (
                        filteredHouseholds.map((h) => (
                          <li
                            key={h.householdId}
                            role="option"
                            aria-selected={filterHouseholdId === h.householdId}
                            className="px-3 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-100 suggestion-item"
                            onMouseDown={() => handleHouseholdSelect(h)}
                          >
                            {householdLabel(h)}
                          </li>
                        ))
                      ) : (
                        <li className="px-3 py-2 text-sm text-gray-500">
                          No {terminology.household.plural.toLowerCase()} match.
                        </li>
                      )}
                    </ul>
                  )}
                </div>
                {householdsError && (
                  <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                    {householdsError}
                    <button
                      type="button"
                      onClick={() => setHouseholdReloadKey(k => k + 1)}
                      className="underline hover:text-red-700"
                    >
                      Retry
                    </button>
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="job-sort" className="block text-xs font-medium text-gray-600">
                  Sort by
                </label>
                <select
                  id="job-sort"
                  name="job-sort"
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-sm md:text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md shadow-sm disabled:bg-gray-100 disabled:text-gray-400"
                  value={jobSortOption}
                  onChange={handleJobSortChange}
                  disabled={loadingJobs || loadingConfig}
                >
                  {JOB_SORT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={handleClearFilters}
                  className="px-3 py-1.5 text-xs md:text-sm font-medium text-gray-700 bg-gray-200 border border-gray-300 rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 disabled:opacity-50"
                  disabled={loadingJobs}
                >
                  <FaTimes className="inline mr-1" /> Clear Filters
                </button>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-4 md:p-8">
              {/* Initial loading message: Show only if no jobs are displayed yet and not an error state */}
              {loadingJobs && jobs.length === 0 && !jobsError && (
                <p className="text-center text-gray-500 py-5">Loading jobs...</p>
              )}
              {/* Error message: Show if an error occurred and no jobs are displayed */}
              {jobsError && jobs.length === 0 && (
                <p className="text-center text-red-500 bg-red-100 p-3 rounded">Error loading jobs: {jobsError}</p>
              )}

              {/* Job list container: Render if jobs exist, or if not loading and no error (for "no jobs found") */}
              {/* This ensures existing jobs remain visible during "load more" */}
              {(jobs.length > 0 || (!loadingJobs && !jobsError)) && (
                <div className="space-y-4">
                  {missedInfo.truncated && selectedStatus === 'approved' && (
                    <p className="text-xs text-gray-500 bg-yellow-50 border border-yellow-200 rounded p-2">
                      Showing the 100 most recent missed jobs ({missedInfo.total} in total).
                    </p>
                  )}
                  {jobs.length === 0 ? (
                    // This renders if jobs.length is 0, !loadingJobs, and !jobsError
                    <p className="text-center text-gray-500 p-6 bg-gray-50 rounded shadow-sm">
                      {`No jobs found ${
                        filterHouseholdId || selectedStatus !== ''
                          ? 'matching your filters'
                          : `with status: ${statusFilterOptions.find(s => s.value === selectedStatus)?.label || 'All Statuses'}`
                      }.`}
                    </p>
                  ) : (
                    jobs.map((job) => {
                      const isFixedFee = job.allocationType === 'fixed-fee';

                      // The scheduled date and time stop being relevant once the job is done, so they are hidden.
                      const isJobFinished = job.status === 'completed' || job.status === 'invoiced';

                      // --- Overdue Check ---
                      const jobDateAsDate = getJobDateValue(job, 'jobDate');
                      const todayStart = new Date(new Date().setHours(0, 0, 0, 0)); // Define today's start date for comparison
                      const isOverdue = jobDateAsDate && jobDateAsDate < todayStart;

                      // --- End Overdue Check ---

                      // --- Generate Google Maps URL ---
                      const googleMapsUrl = getGoogleMapsUrl(job.householdInfo);
                      const hasAddress = job.householdInfo?.addressLine1 || job.householdInfo?.addressLine2 || job.householdInfo?.state || job.householdInfo?.postalCode;
                      const addressText = hasAddress
                        ? `${job.householdInfo?.addressLine1 || ''}${
                            job.householdInfo?.addressLine2 ? ', ' + job.householdInfo.addressLine2 : ''
                          }, ${job.householdInfo?.state || ''} ${job.householdInfo?.postalCode || ''}`
                          .replace(/^, /, '')
                          .replace(/, $/, '')
                          .trim()
                        : 'No address';
                      // --- End Google Maps URL Generation ---

                      // --- Client phone (tappable tel: link) ---
                      const householdPhone = (job.householdInfo?.phoneNumber || '').trim(); // stored formatting for display
                      const householdPhoneHref = householdPhone.replace(/[^\d+]/g, ''); // digits/+ only for tel:, empty when not dialable
                      // --- End Client phone ---

                      // --- Determine Rate/Quote to display ---
                      const isPendingQuoteOrApproval = job.status === 'pending-quote' || job.status === 'pending-approval';
                      let rateDisplay = 'N/A';
                      if (isPendingQuoteOrApproval && job.priceQuotePerHour != null) {
                        rateDisplay = `$${job.priceQuotePerHour.toFixed(2)} / hr (Quote Incl. GST)`;
                      } else if (isFixedFee && job.pricePerHour != null) {
                        rateDisplay = `$${job.pricePerHour.toFixed(2)} (Fixed Incl. GST)`;
                      } else if (job.pricePerHour != null) {
                        rateDisplay = `$${job.pricePerHour.toFixed(2)} / hr (Incl. GST)`;
                      }
                      // --- End Rate/Quote determination ---

                      return (
                        <Link key={job.id} to={`/job/${job.id}`} className="block hover:bg-gray-100 rounded-lg transition duration-150 ease-in-out">
                          <div className="bg-gray-50 shadow-md rounded-lg p-4 border border-gray-200 relative">
                            {/* --- Overdue Message --- */}
                            {/* Show only if overdue AND status is approved */}
                            {isOverdue && job.status === 'approved' && (
                              <p className="text-xs text-red-600 mb-2 pb-2 border-b border-red-200 flex items-center">
                                <FaExclamationTriangle className="mr-1" />
                                Job is overdue. Reschedule or update to 'Completed' if the job is done.
                              </p>
                            )}
                            {/* --- End Overdue Message --- */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600 mb-3 pb-3 border-b border-gray-300">
                              <div>
                                <p className="font-semibold mb-1">
                                  <FaHome className="inline mr-1 text-gray-500" />
                                  {terminology.household.singular}
                                </p>
                                <p>{`${job.householdInfo?.firstName || ''} ${job.householdInfo?.lastName || ''}`.trim() || 'N/A'}</p>
                                {/* Updated Address Display */}
                                <div className="text-xs text-gray-500 flex items-start">
                                  <FaMapMarkerAlt className="inline mr-1 mt-0.5 flex-shrink-0" />
                                  {googleMapsUrl ? (
                                    <a
                                      href={googleMapsUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:underline"
                                      title="Open address in Google Maps"
                                      onClick={(e) => e.stopPropagation()} // Prevent Link navigation when clicking address
                                    >
                                      {addressText}
                                    </a>
                                  ) : (
                                    <span>{addressText}</span>
                                  )}
                                </div>
                                {/* End Updated Address Display */}
                                {/* --- Client Phone (hidden entirely when the job has no phone) --- */}
                                {householdPhoneHref && (
                                  <a
                                    href={`tel:${householdPhoneHref}`}
                                    className="inline-flex items-center gap-1.5 min-h-[44px] pr-2 text-sm font-medium text-blue-600 hover:underline rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    title="Call this client"
                                    onClick={(e) => e.stopPropagation()} // Prevent card Link navigation when tapping the number
                                  >
                                    <FaPhoneAlt className="flex-shrink-0 text-gray-500" aria-hidden="true" />
                                    {householdPhone}
                                  </a>
                                )}
                                {/* --- End Client Phone --- */}
                              </div>
                              <div>
                                <p className="font-semibold mb-1">
                                  <FaUser className="inline mr-1 text-gray-500" />
                                  {terminology.client.singular}
                                </p>
                                <p>{job.clientInfo?.clientName || 'N/A'}</p>
                              </div>
                            </div>

                            <div className="text-sm text-gray-700 space-y-1 flex-grow">
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                <FaRedo className="text-gray-500" />
                                <span className="font-medium text-gray-600">Status:</span>
                                <StatusBadge statusCode={job.status} />
                                {/* --- Moved Completion Date Display Here --- */}
                                {job.status === 'completed' && job.completionDate && (
                                  <span className="text-sm text-gray-500 ml-1">
                                    Service Completed on {formatTimestamp(job.completionDate)}
                                  </span>
                                )}
                                {/* --- End Moved Completion Date Display --- */}
                                {/* --- Invoiced On Date (completed/invoiced jobs with an RCTI date) --- */}
                                {(job.status === 'completed' || job.status === 'invoiced') && job.rctiCreatedAt && (
                                  <span className="text-sm text-gray-500 ml-1">
                                    Invoiced on {formatTimestamp(job.rctiCreatedAt)}
                                  </span>
                                )}
                                {/* --- RCTI View Link (completed/invoiced jobs with an RCTI) --- */}
                                {(job.status === 'completed' || job.status === 'invoiced') && job.rctiId && (
                                  <button
                                    type="button"
                                    onClick={(e) => handleViewRcti(e, job)}
                                    disabled={rctiStatus[job.id]?.loading}
                                    className="inline-flex items-center text-sm font-medium text-blue-600 hover:underline disabled:opacity-50 disabled:cursor-wait"
                                    title="View or download the RCTI for this job"
                                  >
                                    <FaFileInvoiceDollar className="mr-1" />
                                    {rctiStatus[job.id]?.loading
                                      ? 'Opening RCTI…'
                                      : `View RCTI${job.rctiNumber ? ` (${job.rctiNumber})` : ''}`}
                                  </button>
                                )}
                                {/* --- End RCTI View Link --- */}
                              </div>
                              {rctiStatus[job.id]?.error && (
                                <p className="text-xs text-orange-700 bg-orange-100 p-1 rounded border border-orange-200">
                                  <FaInfoCircle className="inline mr-1 text-orange-500" />
                                  {rctiStatus[job.id].error}
                                </p>
                              )}
                              {/* --- Reason for Delay Display --- */}
                              {job.status === 'service-delayed' && job.reasonForDelay && (
                                <p className="text-xs text-orange-700 bg-orange-100 p-1 rounded border border-orange-200">
                                  <FaInfoCircle className="inline mr-1 text-orange-500" />
                                  Delay Reason: {job.reasonForDelay}
                                </p>
                              )}
                              {/* --- End Reason for Delay Display --- */}
                              {/* Skipped entirely on a finished job with no schedule, so the card does not keep an empty line. */}
                              {(!isJobFinished || job.scheduleType) && (
                                <p>
                                  {!isJobFinished && (
                                    <>
                                      <FaCalendarAlt className="inline mr-1 text-green-600" />
                                      <span className={`mr-3 ${isOverdue && job.status === 'approved' ? 'text-red-600' : ''}`}>
                                        Scheduled for: {formatTimestamp(job.jobDate)}
                                      </span>

                                      <FaClock className="inline mr-1 text-green-600" />
                                      <span className="mr-3">Time: {job.jobTime || 'N/A'}</span>
                                    </>
                                  )}

                                  {job.scheduleType && (
                                    <span className="mr-3 capitalize">
                                      <FaCalendarCheck className="inline mr-1 text-blue-600" />
                                      {/* Updated Schedule Display Logic */}
                                      {job.scheduleType === 'recurring' && job.frequencyType
                                        ? formatFrequency(job.frequencyType, job.frequencyValue) // Show only formatted frequency for recurring
                                        : job.scheduleType.replace('-', ' ') // Show schedule type for non-recurring (e.g., "One off")
                                      }
                                    </span>
                                  )}
                                </p>
                              )}
                              <p>
                                {/* --- Combined Allocation Display --- */}
                                <FaHourglassHalf className="inline mr-1 text-green-600" />
                                <span className="mr-3">
                                  Allocation: {formatAllocationDetails(job)}
                                </span>
                                {/* --- End Combined Allocation Display --- */}

                                {/* --- Actual Hours Display (Conditional Hide for Fixed-Fee) --- */}
                                {!isFixedFee && (
                                  <span className="mr-3"> {/* Added mr-3 for spacing */}
                                    <FaHourglassHalf className="inline mr-1 text-green-600" />
                                    <span>Actual: {job.actualHours != null ? `${job.actualHours} hr(s)` : 'N/A'}</span>
                                  </span>
                                )}
                                {/* --- End Actual Hours Display --- */}

                                {/* --- Updated Rate/Quote Display --- */}
                                <span className="mr-3"> {/* Added mr-3 for spacing */}
                                    <FaDollarSign className="inline mr-1 text-green-600" />
                                    Rate: {rateDisplay}
                                </span>
                                {/* --- End Updated Rate/Quote Display --- */}
                              </p>
                              {job.services && job.services.length > 0 && (
                                <p className="text-xs">
                                  <FaTasks className="inline mr-2 text-green-600" />
                                  {/* Updated service display logic */}
                                  Services: {job.services?.map(s => s.name).join(', ')}
                                </p>
                              )}
                              {job.isNoPhotoJob && (
                                <p className="text-xs text-gray-600 italic">
                                  <MdOutlineImageNotSupported className="inline mr-2 text-gray-500" />
                                  No photos required for this job
                                </p>
                              )}
                              {job.remarks && (
                                <p className="text-xs mt-1 pt-1 border-t border-gray-200">
                                  <FaCommentAlt className="inline mr-2 text-gray-500" />
                                  Remarks: {job.remarks}
                                </p>
                              )}
                            </div>
                          </div>
                        </Link>
                      );
                    })
                  )}

                  {/* Load More Button / Loading More Indicator / No More Jobs Message / Retry for My Jobs */}
                  {activeTab === 'myJobs' && (
                    <>
                      {/* "Loading more jobs..." indicator: shown if loading, jobs exist, and no overriding error */}
                      {loadingJobs && jobs.length > 0 && !jobsError && (
                        <p className="text-center text-gray-500 mt-4 py-5">Loading more jobs...</p>
                      )}

                      {/* Load More Button: shown if not loading, there's a next page, no "no more" message, and no error */}
                      {!loadingJobs && nextPageDocId && !noMoreJobsMessage && !jobsError && (
                        <div className="mt-6 text-center">
                          <button
                            onClick={handleLoadMoreJobs}
                            className="px-6 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                          >
                            Load More Jobs
                          </button>
                        </div>
                      )}

                      {/* "No more jobs to load" message: shown if not loading, message exists, and no error */}
                      {!loadingJobs && noMoreJobsMessage && !jobsError && (
                        <p className="text-center text-gray-500 mt-4 py-5">{noMoreJobsMessage}</p>
                      )}
                      
                      {/* Error and Retry for "Load More" if jobs were already displayed */}
                      {jobsError && jobs.length > 0 && (
                        <div className="mt-4 text-center">
                            <p className="text-red-500 bg-red-100 p-3 rounded mb-2">Error loading more jobs: {jobsError}</p>
                            {/* Offer retry only if it seemed like there was more to load (nextPageDocId was likely present before error) */}
                            {/* fetchJobs doesn't nullify nextPageDocId on error if loadMore is true, so it should reflect pre-error state */}
                            {nextPageDocId && ( 
                                <button
                                    onClick={() => {
                                        // fetchJobs will clear jobsError internally when called by handleLoadMoreJobs
                                        handleLoadMoreJobs(); 
                                    }}
                                    className="px-6 py-2 text-sm font-medium text-white bg-orange-500 border border-transparent rounded-md shadow-sm hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-400"
                                >
                                    Retry Load More
                                </button>
                            )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === 'pendingQuotes' && (
          <div className="bg-white rounded-lg shadow-lg p-4 md:p-8">
            {/* Sub Tabs Navigation for Pending Quotes */}
            <div className="mb-4 border-b border-gray-200">
              <nav className="-mb-px flex space-x-8" aria-label="Quote Sub Tabs">
                <button
                  onClick={() => handleQuoteSubTabChange('pendingYourQuotation')}
                  className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                    activeQuoteSubTab === 'pendingYourQuotation'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Pending Your Quotation
                </button>
                <button
                  onClick={() => handleQuoteSubTabChange('pendingApproval')}
                  className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                    activeQuoteSubTab === 'pendingApproval'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Pending Approval
                </button>
              </nav>
            </div>

            {/* Content for "Pending Your Quotation" sub-tab */}
            {activeQuoteSubTab === 'pendingYourQuotation' && (
              <PendingQuotationList
                schedules={pendingYourQuotationSchedules}
                loading={loadingPendingYourQuotation}
                error={pendingYourQuotationError}
                initialLoaded={initialPendingYourQuotationLoaded}
                pricePerHourInputs={pricePerHourInputs}
                notesForAdminInputs={notesForAdminInputs}
                frequencyInputs={frequencyInputs}
                isSubmittingQuote={isSubmittingQuote}
                quoteSubmitError={quoteSubmitError}
                nextPageDocId={nextPageDocIdPendingYourQuotation}
                onPricePerHourChange={handlePricePerHourChange}
                onNotesForAdminChange={handleNotesForAdminChange}
                onFrequencyChange={handleFrequencyChange}
                onQuoteSubmit={handleQuoteSubmit}
                onLoadMore={handleLoadMorePendingYourQuotation}
                isReadOnly={isAdminView || isSuspended}
              />
            )}

            {/* Content for "Pending Approval" sub-tab */}
            {activeQuoteSubTab === 'pendingApproval' && (
              <>
                {loadingPendingApproval && pendingApprovalSchedules.length === 0 && <p className="text-center text-gray-500 py-5">Loading schedules pending approval...</p>}
                {pendingApprovalError && <p className="text-center text-red-500 bg-red-100 p-3 rounded">Error: {pendingApprovalError}</p>}
                {!loadingPendingApproval && !pendingApprovalError && pendingApprovalSchedules.length === 0 && initialPendingApprovalLoaded && (
                  <p className="text-center text-gray-500 py-5">No jobs found pending approval.</p>
                )}
                {pendingApprovalSchedules.length > 0 && (
                  <div className="space-y-4">
                    {pendingApprovalSchedules.map((schedule) => {
                      const isFixedFee = schedule.allocationType === 'fixed-fee';
                      let allottedHoursLabel = 'Allotted';
                      if (schedule.allocationType === 'monthly') {
                        allottedHoursLabel = 'Allocation per month';
                      } else if (schedule.allocationType === 'per-visit') {
                        allottedHoursLabel = 'Allocation per visit';
                      }
                      return (
                        <div key={schedule.id} className="bg-gray-50 shadow-md rounded-lg p-4 border border-gray-200">
                          {/* Row 1: Client and Household Info */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600 mb-3 pb-3 border-b">
                            <div>
                              <p className="font-semibold mb-1"><FaHome className="inline mr-1 text-gray-500"/>{terminology.household.singular}</p>
                              <p>{`${schedule.householdInfo?.firstName || ''} ${schedule.householdInfo?.lastName || ''}`.trim() || 'N/A'}</p>
                              <p className="text-xs text-gray-500"><FaMapMarkerAlt className="inline mr-1"/>{`${schedule.householdInfo?.addressLine1 || ''}${schedule.householdInfo?.state ? ', ' + schedule.householdInfo.state : ''} ${schedule.householdInfo?.postalCode || ''}`.replace(/^, /,'').replace(/, $/,'').trim() || 'No address'}</p>
                            </div>
                            <div>
                              <p className="font-semibold mb-1"><FaUser className="inline mr-1 text-gray-500"/>{terminology.client.singular}</p>
                              <p>{schedule.clientInfo?.clientName || 'N/A'}</p>
                            </div>
                          </div>
                          {/* Row 2: Schedule Details and Time */}
                          <div className="text-sm text-gray-700 space-y-1 mb-3">
                              <p className="font-semibold capitalize"><FaCalendarAlt className="inline mr-2 text-jims-cyan" />{formatTimestamp(schedule.startDate)}
                                {schedule.jobTime && (<span className="ml-3"><FaClock className="inline mr-1 text-jims-cyan" />Preferred Time: {schedule.jobTime}</span>)}
                              </p>
                          </div>
                          {/* Row 3: Allocation, Hours, Services, Notes, and Submitted Price */}
                          <div className="text-sm text-gray-700 space-y-1 mb-3">
                              <p>
                                  {schedule.allocationType && (<span className="mr-3 inline-flex items-center"><FaLayerGroup className="inline mr-1 text-jims-cyan" />Allocation: {formatAllocationType(schedule.allocationType)}</span>)}
                                  {/* Hours are hidden on a set fee: there allotedHours is not an allocation but the invoice
                                      quantity (quote / supplier hourly rate), so it gives away the supplier's rate by division. */}
                                  {!isFixedFee && schedule.allotedHours != null && (<span className="mr-3 inline-flex items-center"><FaHourglassHalf className="mr-1 text-jims-cyan" />
                                    {allottedHoursLabel}: {schedule.allotedHours} hr(s)</span>)}
                              </p>
                              {schedule.services && schedule.services.length > 0 && (<p><FaTasks className="inline mr-1 text-jims-cyan" />Services: {schedule.services.map(s => s.name).join(', ')}</p>)}
                              {schedule.notesForFranchisee && (<p><FaStickyNote className="inline mr-1 text-jims-cyan" />Notes: <span className="text-gray-600 italic">{schedule.notesForFranchisee}</span></p>)}
                              {schedule.pricePerHour != null && ( // Corrected: Moved <p> inside this condition and fixed structure
                                <p className="mt-2 pt-2 border-t border-gray-200">
                                  <FaDollarSign className="inline mr-1 text-jims-green" />
                                  Submitted Quote: <span className="font-semibold">${schedule.pricePerHour.toFixed(2)} (Incl. GST)
                                  {schedule.allocationType !== 'fixed-fee' ? ' / hr' : ' (Fixed Total)'}</span>
                                </p>
                              )}
                          </div>
                          {/* Description is hidden: it is auto-generated from the supplier hourly rate, which franchisees must not see. */}
                        </div>
                      );
                    })}
                    {nextPageDocIdPendingApproval && ( // Corrected: Button directly inside this condition
                      <div className="mt-6 text-center">
                        <button onClick={handleLoadMorePendingApproval} disabled={loadingPendingApproval} className="px-6 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50">
                          {loadingPendingApproval ? 'Loading...' : 'Load More Pending Approval'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

         {/* Compliance Tab */}
        {activeTab === 'compliance' && (
          <ComplianceTab userInfo={userInfo} />
        )}
      </div>
      <style jsx>{`
        .suggestion-item:hover {
          background-color: #f3f4f6;
        }
      `}</style>

     

      {/* Add Quote Confirmation Modal */}
      {showQuoteConfirmation && quoteToConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h4 className="text-lg font-semibold mb-4 text-gray-800">Confirm Quote Submission</h4>
            <p className="text-sm text-gray-700 mb-4">
              Are you sure you want to submit this quote?
              <br />
              <span className="font-semibold">
                Quote Amount: ${quoteToConfirm.pricePerHour.toFixed(2)} (Incl. GST)
                {pendingYourQuotationSchedules.find(s => s.id === quoteToConfirm.scheduleId)?.allocationType !== 'fixed-fee' ? ' / hr' : ' (Fixed Total)'}
              </span>
            </p>
            {/* GST Information Alert */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    <strong>Important:</strong> This quotation includes GST. Please ensure your pricing reflects the total amount including GST.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowQuoteConfirmation(false);
                  setQuoteToConfirm(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 border border-gray-300 rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={confirmAndSubmitQuote}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center"
              >
                Submit Quote
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
